import { where, type QueryFieldFilterConstraint } from 'firebase/firestore';

// The locations a manager oversees. `users.assigned_locations` lists them and
// is the field of record: once it holds anything, `assigned_location` (kept in
// step as its first, primary entry) is ignored. Accounts set up before a
// manager could oversee several sites carry only `assigned_location`.
// firestore.rules' managedLocations() resolves the same two fields the same
// way, so a query filtered by this list is one the rules can prove.

export interface LocationFields {
  assigned_location?: string | null;
  assigned_locations?: ReadonlyArray<string | null | undefined> | null;
}

const isLocation = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** The user's locations, primary first; empty when the account has none. */
export function managedLocations(metadata: LocationFields | null | undefined): string[] {
  const list = metadata?.assigned_locations;
  // Like the rules, any non-empty list wins; its blank entries are then dropped.
  if (Array.isArray(list) && list.length > 0) return Array.from(new Set(list.filter(isLocation)));
  const single = metadata?.assigned_location;
  return isLocation(single) ? [single] : [];
}

/**
 * A query filter on `field` matching any of `locations`: plain equality for one
 * (the shape every single-site manager has always queried with), `in` for
 * several. Firestore allows up to 30 `in` values, far more than there are sites.
 */
export function whereLocation(field: string, locations: readonly string[]): QueryFieldFilterConstraint {
  return locations.length === 1 ? where(field, '==', locations[0]) : where(field, 'in', [...locations]);
}

/** Display text for a set of locations, e.g. "one-north + City Hall". */
export function joinLocationLabels(locations: readonly string[], label: (location: string) => string): string {
  return locations.map(label).join(' + ');
}
